import { createContext, useContext } from 'react';
import type { ViewSource } from '@secretario/shared';

/**
 * De onde veio o painel aberto e do que se fala nele. "Nada muda sem origem
 * visível": quando é o Secretário que abre, o painel mostra o pedido entendido.
 */
export type StageOrigin = {
  source: ViewSource;
  /** O pedido, nas palavras da pessoa. */
  asked?: string;
  /** Nome do item citado: a linha dele pulsa uma vez. */
  focus?: string;
  /** Volta ao painel que estava aberto antes de o Secretário trocar a tela. */
  back?: () => void;
};

export const StageOriginContext = createContext<StageOrigin | null>(null);

export function useStageOrigin(): StageOrigin | null {
  return useContext(StageOriginContext);
}

const fold = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();

/** A linha é a citada? Compara sem acento e sem caixa, nos dois sentidos. */
export function isCited(focus: string | undefined, title: string | null | undefined): boolean {
  if (!focus || !title) return false;
  const a = fold(focus);
  const b = fold(title);
  if (a.length < 3 || b.length < 3) return a === b;
  return a.includes(b) || b.includes(a);
}

/** Classe da linha citada (pulsa uma vez) para somar à classe da linha. */
export function citedClass(focus: string | undefined, title: string | null | undefined): string {
  return isCited(focus, title) ? ' stg-cited' : '';
}

/** `ref` da linha citada: garante que ela esteja à vista quando o painel abre. */
export function citedRef(focus: string | undefined, title: string | null | undefined) {
  if (!isCited(focus, title)) return undefined;
  return (element: HTMLElement | null) => {
    element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };
}
